import React, { useEffect, useState } from "react";
import { GetStoresRatingData } from "../../lib/data"; // Import your API function
import Pagination from "../../ui/paginations/Paginations"; // Reuse your existing pagination

// Helper component for Stars
const StarRating = ({ rate, size = "md" }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`${
          size === "lg" ? "w-6 h-6" : "w-4 h-4"
        } ${i <= rate ? "text-yellow-400" : "text-gray-300"}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex gap-1">{stars}</div>;
};

export default function StoreRatingsModal({ storeId, isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rate: 0, ratings: [] });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (isOpen && storeId) {
      fetchRatings();
    }
  }, [isOpen, storeId, currentPage]);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      // Calling your API logic
      const response = await GetStoresRatingData(storeId, currentPage);
      setData(response.data || { rate: 0, ratings: [] });
      setPagination(response.pagination || { current_page: 1, last_page: 1 });
    } catch (error) {
      console.error("Failed to fetch ratings", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Store Reviews</h2>
            <p className="text-sm text-gray-500">Store ID: #{storeId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-gray-400">Loading reviews...</p>
            </div>
          ) : (
            <>
              {/* Overall Rating Card */}
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6 flex items-center gap-6">
                <div className="text-center px-4 border-r border-gray-100">
                    <span className="block text-4xl font-extrabold text-gray-800">{data.rate}</span>
                    <span className="text-xs text-gray-400 uppercase tracking-wide">Average</span>
                </div>
                <div className="flex flex-col gap-1">
                    <StarRating rate={Math.round(Number(data.rate))} size="lg" />
                    <p className="text-sm text-gray-500">{pagination.total} Total Ratings</p>
                </div>
              </div>

              {/* Reviews List */}
              <div className="space-y-4">
                {data.ratings && data.ratings.length > 0 ? (
                  data.ratings.map((review) => (
                    <div key={review.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
                      {/* Review Header */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                           <div className="bg-blue-50 text-blue-600 rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm">
                              #{review.id}
                           </div>
                           <div>
                              <StarRating rate={review.rate} />
                              <span className="text-xs text-gray-400 mt-1 block">{review.date} at {review.time}</span>
                           </div>
                        </div>
                        <div className="text-right">
                           <span className="block font-bold text-gray-800">{Number(review.total).toLocaleString()} <span className="text-xs font-normal text-gray-500">Total</span></span>
                           <span className="block text-xs text-green-600 font-medium">Profit: {Number(review.total_after_profit).toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Comment */}
                      {review.comment && (
                        <div className="bg-gray-50 p-3 rounded-lg mb-4 text-gray-700 text-sm italic border-l-4 border-gray-300">
                          "{review.comment}"
                        </div>
                      )}

                      {/* Items (Products) */}
                      {review.items && review.items.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-xs font-bold text-gray-400 uppercase mb-3">Order Items</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {review.items.map((item) => (
                              <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg border border-gray-100 bg-white">
                                <img 
                                  src={item.photo} 
                                  alt={item.title} 
                                  className="w-12 h-12 rounded-md object-cover bg-gray-200"
                                  onError={(e) => e.target.src = "https://via.placeholder.com/150?text=No+Img"} // Fallback
                                />
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold text-gray-800">{item.title}</h4>
                                  <div className="flex justify-between text-xs mt-1">
                                    <span className="text-gray-500">Qty: {item.quantity}</span>
                                    <span className="font-medium text-gray-900">{Number(item.price).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  // Empty State
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                    <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No ratings found</h3>
                    <p className="mt-1 text-sm text-gray-500">This store hasn't received any reviews yet.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer with Pagination */}
        <div className="p-4 bg-white border-t border-gray-200">
           <Pagination
              currentPage={currentPage}
              totalPages={pagination.last_page}
              handleChangePage={(page) => setCurrentPage(page)}
            />
        </div>
      </div>
    </div>
  );
}